import { CSSProperties } from 'react'

import { AppBreakpoint } from '@makyoapp/makyo-sdk/models/AppBuilder'
import { ActionFlag, IActionRepeaterValue, IActionType, isValidUrl } from '@makyoapp/makyo-common'

import { BuilderComponentSettingValidation } from './builder'
import { SpacingInputValue } from './builder/types/spacing-input'

export function hasActionFlag(flags: ActionFlag, lookup: ActionFlag) {
  return (flags & lookup) === lookup
}

export function removeActionFlag(flags: ActionFlag, flagToRemove: ActionFlag) {
  return flags & ~flagToRemove
}

export const getComponentDirection = (lng: string) => {
  const dir = lng === 'ar' || lng === 'he' ? 'rtl' : 'ltr'
  return dir
}

export const getNavbarStyle = (device: AppBreakpoint): string => {
  return 'navigationbar-' + device
}

/** @deprecated should be removed since makyo-tw-ui is now used */
export function parseSpacing(value?: SpacingInputValue, isMargin?: boolean): CSSProperties {
  const spacing: CSSProperties = {}
  if (typeof value === 'undefined') {
    return {}
  }

  if (!isMargin) {
    spacing.paddingTop = `${value.top.value}${value.top.unit}`
    spacing.paddingInlineEnd = `${value.right.value}${value.right.unit}`
    spacing.paddingBottom = `${value.bottom.value}${value.bottom.unit}`
    spacing.paddingInlineStart = `${value.left.value}${value.left.unit}`
  } else {
    spacing.marginTop = `${value.top.value}${value.top.unit}`
    spacing.marginInlineEnd = `${value.right.value}${value.right.unit}`
    spacing.marginBottom = `${value.bottom.value}${value.bottom.unit}`
    spacing.marginInlineStart = `${value.left.value}${value.left.unit}`
  }

  return spacing
}

export function propsFromActions(
  actions: IActionRepeaterValue[] | Omit<IActionRepeaterValue, 'id'>,
  props: Record<string, unknown> = {}
) {
  if (Array.isArray(actions)) {
    for (const act of actions) {
      propsFromActions(act, props)
    }
  } else {
    switch (actions.type) {
      case IActionType.OpenLink:
        props.target = '_blank'
        props.href = actions.settings.value
        break
      case IActionType.OpenMap:
        props.target = '_blank'
        props.href = `https://www.google.com/maps/place/${
          actions.settings?.value?.toString().replace(/\s/g, '+') ?? ''
        }`
        break
      default:
        break
    }
  }
}

export function twProse(): CSSProperties {
  return {
    '--color-fg-default': 'var(--tw-ui-text-active)',
    '--color-canvas-subtle': 'var(--tw-ui-rich-text-code-bg)',
    '--color-canvas-default': 'transparent',
    '--color-border-default': 'var(--md-sys-color-outline)',
    '--color-accent-fg': 'var(--md-sys-color-primary)',
    '--color-border-muted': 'transparent',
    '--color-fg-muted': 'color-mix(in oklab, currentColor 80%, transparent)',
    '--tw-ui-border-mid': 'color-mix(in oklab, currentColor 80%, transparent)',
    //'--tw-prose-body': '#374151',
    '--tw-prose-body': 'inherit',
    '--tw-prose-headings': 'inherit', // maybe on-surface
    '--tw-prose-lead': '#4b5563',
    '--tw-prose-links': 'inherit', // maybe on-surface
    '--tw-prose-bold': 'inherit', // maybe on-surface
    '--tw-prose-counters': 'var(--md-sys-color-outline-variant)',
    '--tw-prose-bullets': 'var(--md-sys-color-outline-variant)',
    '--tw-prose-hr': 'var(--md-sys-color-outline)',
    '--tw-prose-quotes': 'inherit', // maybe on-surface
    '--tw-prose-quote-borders': 'var(--md-sys-color-outline)',
    '--tw-prose-captions': '#6b7280',
    '--tw-prose-code': 'inherit', // maybe on-surface
    //'--tw-prose-pre-code': '#e5e7eb',
    //'--tw-prose-pre-bg': '#1f2937',
    '--tw-prose-pre-code': 'var(--md-sys-color-on-tertiary)',
    '--tw-prose-pre-bg': 'var(--md-sys-color-tertiary)',
    '--tw-prose-th-borders': 'var(--md-sys-color-outline-variant)',
    //'--tw-prose-td-borders': '#e5e7eb',
    '--tw-prose-td-borders': 'var(--md-sys-color-outline-variant)',
    '--tw-prose-invert-body': 'var(--md-sys-color-outline-variant)',
    '--tw-prose-invert-headings': '#fff',
    '--tw-prose-invert-lead': '#9ca3af',
    '--tw-prose-invert-links': '#fff',
    '--tw-prose-invert-bold': '#fff',
    '--tw-prose-invert-counters': '#9ca3af',
    '--tw-prose-invert-bullets': '#4b5563',
    '--tw-prose-invert-hr': '#374151',
    '--tw-prose-invert-quotes': '#f3f4f6',
    '--tw-prose-invert-quote-borders': '#374151',
    '--tw-prose-invert-captions': '#9ca3af',
    '--tw-prose-invert-code': '#fff',
    '--tw-prose-invert-pre-code': 'var(--md-sys-color-outline-variant)',
    '--tw-prose-invert-pre-bg': 'rgb(0 0 0 / 50%)',
    '--tw-prose-invert-th-borders': '#4b5563',
    '--tw-prose-invert-td-borders': '#374151',
  } as CSSProperties
}

export function arrayFillNumber(count: number, fraction = 0) {
  return Array(count).fill(Number((100 / count).toFixed(fraction)))
}

export function getValidationFn(validation: BuilderComponentSettingValidation) {
  switch (validation) {
    case BuilderComponentSettingValidation.NotEmpty:
      // check if the value is not empty
      return (v: string): string => {
        return v !== '' && v !== undefined && v !== null ? '' : 'Text cannot be empty'
      }
    case BuilderComponentSettingValidation.Number:
      // check if the value is a valid number
      return (v: string): string => {
        return !isNaN(Number(v)) ? '' : 'Invalid number'
      }
    case BuilderComponentSettingValidation.URL:
      // check if the value is a valid URL
      return (v: string): string => {
        return v !== '' && v !== undefined && v !== null ? (isValidUrl(v) ? '' : 'Invalid URL') : ''
      }
    case BuilderComponentSettingValidation.AudioFile:
    case BuilderComponentSettingValidation.VideoFile:
      // check if the value is a valid audio or video file URL
      return (v: string) => {
        const invalidUrl: string = getValidationFn(BuilderComponentSettingValidation.URL)(v)

        const isOembed = ['youtu.be', 'youtube.com', 'vimeo.com', 'soundcloud.com', 'giphy.com'].some((em) =>
          v.includes(em)
        )
        return !invalidUrl && !isOembed && v !== '' && v !== undefined && v !== null
          ? ['.mp3', '.ogg', '.oga', '.ogv', '.ogx', '.spx', '.opus', '.wav', '.wave', '.mp4', '.webm'].some((type) =>
              v.toLowerCase().endsWith(type)
            )
            ? ''
            : 'Invalid File Type'
          : invalidUrl
      }
    case BuilderComponentSettingValidation.LottieFile:
      // check if the value is a valid Lottie file URL
      return (v: string) => {
        const invalidUrl: string = getValidationFn(BuilderComponentSettingValidation.URL)(v)
        const isLottie = v.includes('lottiefiles') || v.includes('lottiehost')
        return !invalidUrl && !isLottie ? '' : 'Invalid URL'
      }
  }
}

/**
 * @deprecated Please use existing parsers from makyo-tw-ui
 */
export function formatMargin(margin: {
  top?: { value: number; unit: string }
  right?: { value: number; unit: string }
  bottom?: { value: number; unit: string }
  left?: { value: number; unit: string }
}): {
  top: string
  right: string
  bottom: string
  left: string
} {
  return {
    top: margin?.top ? `${margin?.top.value}${margin?.top.unit}` : '0px',
    right: margin?.right ? `${margin?.right.value}${margin?.right.unit}` : '0px',
    bottom: margin?.bottom ? `${margin?.bottom.value}${margin?.bottom.unit}` : '0px',
    left: margin?.left ? `${margin?.left.value}${margin?.left.unit}` : '0px',
  }
}
